import userService from 'services/UserService';
import apiService from 'services/ApiService';
import { AuthState } from 'types/user.types';
import { logger } from 'utils/logger';
import { BehaviorSubject, Observable } from 'rxjs';

// Create a service-specific logger
const log = logger.getLogger('AuthService');

class AuthService {
    private authStateSubject = new BehaviorSubject<AuthState>({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null
    });

    private token: string | null = null;
    private idToken: string | null = null;

    constructor() {
        // Register this service with the API service to avoid circular dependency
        apiService.setAuthService(this);
        
        log.log('AuthService initialized');
    }

    /**
     * Get the current auth state as an observable
     */
    getAuthState$(): Observable<AuthState> {
        return this.authStateSubject.asObservable();
    }

    /**
     * Get the current auth state value
     */
    getAuthState(): AuthState {
        return this.authStateSubject.value;
    }

    /**
     * Set authentication state - called from auth provider on login/logout events
     */
    setAuthState(state: Partial<AuthState>): void {
        const currentState = this.authStateSubject.value;
        const newState: AuthState = { ...currentState, ...state };

        this.authStateSubject.next(newState);

        // If user is included, update the user service as well
        if (state.user !== undefined) {
            userService.setUserBasicProfile(state.user);
        }
    }

    /**
     * Set authentication loading state
     */
    setLoading(isLoading: boolean): void {
        this.setAuthState({ isLoading });
    }

    /**
     * Set authentication tokens
     */
    setTokens(token: string | null, idToken: string | null): void {
        this.token = token;
        this.idToken = idToken;

        log.log(`Tokens set in AuthService - Access token present: ${!!token}, ID token present: ${!!idToken}`);

        // Set auth header in axios for future requests - use access token, not ID token
        if (token) {
            apiService.setAuthorizationHeader(idToken);
        } else {
            apiService.setAuthorizationHeader(null);
        }
    }

    /**
     * Get access token
     */
    getToken(): string | null {
        return this.token;
    }

    /**
     * Get ID token
     */
    getIdToken(): string | null {
        return this.idToken;
    }

    /**
     * Check if the user is authenticated
     */
    isAuthenticated(): boolean {
        return this.authStateSubject.value.isAuthenticated;
        // return this.authStateSubject.value.isAuthenticated && !!this.idToken;
    }

    /**
     * Logout user
     */
    logout(): void {
        // Clear tokens
        this.token = null;
        this.idToken = null;
        apiService.setAuthorizationHeader(null);

        // Update auth state
        this.setAuthState({
            isAuthenticated: false,
            user: null
        });

        // Clear user in UserService
        userService.clearUser();

        // Clear API cache when logging out
        apiService.clearCache();

        log.log('User logged out successfully');
    }
}

const authService = new AuthService();
export default authService;